import * as THREE from 'three';
import { Vec3 } from '@/core/math/Vec3';
import { CelestialBody } from '@/universe/CelestialBody';
import {
  GLSL_ATMOSPHERE,
  GLSL_CONSTANTS,
  GLSL_NOISE,
  GLSL_RING_DENSITY,
} from './shaders/common.glsl';
import {
  DENSITY_SCALE_MAX,
  DENSITY_SCALE_MIN,
  DENSITY_SCALE_NOMINAL,
  MIE_SCALE_HEIGHT_FRACTION,
  MIN_SCALE_HEIGHT,
  SUN_COLOR,
  SUN_INTENSITY_DEFAULT,
} from './shaders/atmosphereDefaults';

const VERTEX = /* glsl */ `
varying vec3 vRingPosition;
varying vec3 vRenderPosition;
varying vec3 vRenderNormal;
varying vec3 vPlanetCentre;
varying float vPlanetRadius;

uniform float uPlanetRadiusRatio;

void main() {
  vRingPosition = position;
  vec4 world = modelMatrix * vec4(position, 1.0);
  vRenderPosition = world.xyz;
  vRenderNormal = normalize(mat3(modelMatrix) * normal);
  vPlanetCentre = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
  vPlanetRadius = length(mat3(modelMatrix) * vec3(1.0, 0.0, 0.0)) * uPlanetRadiusRatio;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAGMENT = /* glsl */ `
precision highp float;

${GLSL_CONSTANTS}
${GLSL_NOISE}
${GLSL_RING_DENSITY}
${GLSL_ATMOSPHERE}

varying vec3 vRingPosition;
varying vec3 vRenderPosition;
varying vec3 vRenderNormal;
varying vec3 vPlanetCentre;
varying float vPlanetRadius;

uniform vec3 uColor;
uniform float uOpacity;
uniform float uOuterRatio;
uniform vec3 uSunDirection;
uniform vec3 uSunColor;
uniform float uSunIntensity;
uniform float uPassMin;
uniform float uPassMax;
uniform float uPlanetPhysicalRadius;
uniform float uAtmosphereRadiusRatio;
uniform float uScaleHeight;
uniform float uMieScaleHeight;
uniform vec3 uRayleigh;
uniform float uMie;
uniform vec3 uHazeColor;
uniform float uDensityScale;
uniform float uForegroundAtmosphereRendered;

const int RING_AERIAL_STEPS = 12;
const float RING_AERIAL_SAMPLE_BIAS = 3.0;

float ringAerialWarp(float u) {
  return (exp(RING_AERIAL_SAMPLE_BIAS * u) - 1.0)
    / (exp(RING_AERIAL_SAMPLE_BIAS) - 1.0);
}

// Integrate the actual portion of the camera-to-ring segment inside the
// atmosphere. A mean-altitude approximation cannot work here: from the ground
// the ring is millions of metres away, but almost all optical depth lives in
// the first few kilometres above the observer.
vec2 ringViewOpticalDepth(vec3 origin, vec3 dir, float nearT, float farT) {
  vec2 depth = vec2(0.0);
  float rayLength = farT - nearT;
  vec3 nearPoint = origin + dir * nearT;
  vec3 farPoint = origin + dir * farT;
  bool densestAtStart = length(nearPoint) <= length(farPoint);

  for (int i = 0; i < RING_AERIAL_STEPS; i++) {
    float w0 = ringAerialWarp(float(i) / float(RING_AERIAL_STEPS));
    float w1 = ringAerialWarp(float(i + 1) / float(RING_AERIAL_STEPS));
    float d0 = densestAtStart ? w0 : 1.0 - w1;
    float d1 = densestAtStart ? w1 : 1.0 - w0;
    float t0 = nearT + d0 * rayLength;
    float dt = (d1 - d0) * rayLength;
    vec3 samplePoint = origin + dir * (t0 + dt * 0.5);
    float altitude = max((length(samplePoint) - 1.0) * uPlanetPhysicalRadius, 0.0);
    float stepMetres = dt * uPlanetPhysicalRadius;
    depth.x += exp(-altitude / max(uScaleHeight, AERIAL_SCALE_HEIGHT_FLOOR))
      * stepMetres * uDensityScale;
    depth.y += exp(-altitude / max(uMieScaleHeight, AERIAL_SCALE_HEIGHT_FLOOR))
      * stepMetres * uDensityScale;
  }
  return depth;
}

vec3 applyRingAerialPerspective(
  vec3 sourceColour,
  vec3 L,
  out float atmosphericVisibility,
  out float foregroundAtmosphere
) {
  atmosphericVisibility = 1.0;
  foregroundAtmosphere = 0.0;
  if (uDensityScale <= 0.0 || uAtmosphereRadiusRatio <= 1.0) return sourceColour;

  // Work in planet-radius units, so this remains numerically stable in both
  // the metre-scale local renderer and the compressed astronomical renderer.
  vec3 rayOrigin = (cameraPosition - vPlanetCentre) / max(vPlanetRadius, 1e-6);
  vec3 rayEnd = (vRenderPosition - vPlanetCentre) / max(vPlanetRadius, 1e-6);
  vec3 rayDelta = rayEnd - rayOrigin;
  float fragmentDistance = length(rayDelta);
  if (fragmentDistance <= 1e-6) return sourceColour;
  vec3 rayDir = rayDelta / fragmentDistance;

  vec2 atmosphereHit = raySphere(rayOrigin, rayDir, uAtmosphereRadiusRatio);
  if (atmosphereHit.y <= 0.0) return sourceColour;
  float nearT = max(atmosphereHit.x, 0.0);
  float farT = min(atmosphereHit.y, fragmentDistance);

  // A far-side ring fragment should already fail the depth test, but clipping
  // against the datum sphere prevents integrating air through solid ground in
  // edge cases where terrain lies below that datum.
  vec2 planetHit = raySphere(rayOrigin, rayDir, 1.0);
  if (planetHit.x > 0.0) farT = min(farT, planetHit.x);
  if (farT <= nearT) return sourceColour;

  vec2 viewDepth = ringViewOpticalDepth(rayOrigin, rayDir, nearT, farT);
  vec3 extinction = exp(-(
    uRayleigh * viewDepth.x + vec3(uMie) * AERIAL_MIE_OPTICAL_DEPTH_GAIN * viewDepth.y
  ));

  // The atmosphere shell is already present behind this transparent sheet.
  // Once the foreground column becomes optically thick, fading the ring's
  // alpha reveals that shell exactly; merely tinting the ring colour leaves a
  // darker silhouette because the two shaders are independent integrations.
  // Luminance keeps the red twilight transmission from making a blue ring
  // remain visible through an otherwise opaque horizon.
  float viewTransmission = dot(extinction, vec3(0.2126, 0.7152, 0.0722));
  atmosphericVisibility = smoothstep(0.035, 0.32, viewTransmission);

  // A local planet already rendered its atmosphere shell into the framebuffer
  // before this ring.  In that case the ring must only ADD the radiance that
  // survives the foreground air; using ordinary alpha would subtract the
  // brighter sky and leave a dark ring-shaped cut-out.  The fragment output
  // below continuously changes from normal premultiplied source-over in
  // vacuum to additive transmitted radiance once a meaningful air column is
  // present.  Astronomical impostors have no separate atmosphere shell and
  // therefore keep the self-contained extinction + in-scatter path below.
  float opticalOpacity = 1.0 - viewTransmission;
  foregroundAtmosphere = uForegroundAtmosphereRendered
    * smoothstep(0.002, 0.04, opticalOpacity);
  if (uForegroundAtmosphereRendered > 0.5) {
    atmosphericVisibility = 1.0;
    return sourceColour * extinction;
  }

  // The atmosphere shell has already drawn the sky behind this transparent
  // sheet. Put the same in-scattered radiance into the ring source colour so
  // alpha blending preserves that foreground haze instead of cutting a clean,
  // high-contrast ring-shaped hole through it.
  vec3 nearPoint = rayOrigin + rayDir * nearT;
  vec3 farPoint = rayOrigin + rayDir * farT;
  vec3 densePoint = length(nearPoint) <= length(farPoint) ? nearPoint : farPoint;
  float sunElevation = dot(normalize(densePoint), L);
  float sunUp = clamp(sunElevation * 0.5 + 0.5, 0.0, 1.0);
  float sunlitAir = smoothstep(AERIAL_SUNLIT_AIR_START, AERIAL_SUNLIT_AIR_END, sunElevation);
  vec3 inscatter = uHazeColor * uSunColor * uSunIntensity * (1.0 - extinction)
    * (AERIAL_MULTI_SCATTER_FLOOR + sunUp * AERIAL_INSCATTER_SUNUP_WEIGHT) * sunlitAir;

  float sunAirmass = 1.0 / max(sunElevation, AERIAL_SUN_BEAM_SIN_FLOOR);
  vec3 beamTau = (uRayleigh + vec3(uMie) * AERIAL_MIE_OPTICAL_DEPTH_GAIN)
    * uDensityScale * uScaleHeight * (sunAirmass - 1.0);
  inscatter *= exp(-beamTau);

  return sourceColour * extinction + inscatter;
}

void main() {
  float rasterDistance = length(vRenderPosition - cameraPosition);
  if (rasterDistance < uPassMin || rasterDistance >= uPassMax) discard;

  float ringRadius = length(vRingPosition.xy);
  float radial = clamp((ringRadius - 1.0) / max(uOuterRatio - 1.0, 1e-5), 0.0, 1.0);
  float aa = max(fwidth(radial) * 1.5, 0.00035);

  float broad;
  float density = ringDensityProfile(radial, aa, broad);
  if (density < 0.004) discard;

  vec3 normal = normalize(vRenderNormal);
  vec3 viewDir = normalize(cameraPosition - vRenderPosition);
  float viewCos = max(abs(dot(normal, viewDir)), 0.08);
  // A geometrically thin sheet becomes optically thicker toward edge-on views.
  float alpha = 1.0 - exp(-uOpacity * density / viewCos);

  vec3 L = normalize(uSunDirection);
  float signedSunFacing = dot(normal, L);
  float signedViewFacing = dot(normal, viewDir);
  float sunFacing = abs(signedSunFacing);
  // Individual ice grains still backscatter at low solar incidence; using a
  // pure Lambert term would make Aegis' low-tilt rings almost black all year.
  float particleScatter = 0.22 + 0.78 * sqrt(sunFacing);
  // A particulate ring is double-sided but not equally bright from both
  // sides. Looking at the sun-facing face shows reflected light; from the
  // opposite side we mostly see attenuated light transmitted through the
  // sheet. Dense bands transmit less than sparse gaps.
  float seesSunlitFace = step(0.0, signedSunFacing * signedViewFacing);
  float transmittedFace = mix(0.42, 0.16, clamp(density, 0.0, 1.0));
  float faceIllumination = mix(transmittedFace, 1.0, seesSunlitFace);
  vec3 planetToRing = vRenderPosition - vPlanetCentre;
  float axisDistance = length(planetToRing - L * dot(planetToRing, L));
  float penumbra = max(vPlanetRadius * 0.015, fwidth(axisDistance) * 1.5);
  float behindPlanet = dot(planetToRing, L) < 0.0 ? 1.0 : 0.0;
  float planetShadow = behindPlanet
    * (1.0 - smoothstep(vPlanetRadius - penumbra, vPlanetRadius + penumbra, axisDistance));
  float directIllumination = particleScatter * faceIllumination * min(uSunIntensity, 2.0) * 0.50;
  float illumination = 0.012 + directIllumination * mix(1.0, 0.025, planetShadow);
  float warmBand = 0.5 + 0.5 * sin(2.0 * PI * (radial * 3.0 + 0.18));
  vec3 bandTint = mix(vec3(0.76, 0.68, 0.57), vec3(1.08, 1.0, 0.86), warmBand);
  vec3 colour = uColor * bandTint * (0.62 + broad * 0.38)
    * uSunColor * illumination;
  float atmosphericVisibility;
  float foregroundAtmosphere;
  colour = applyRingAerialPerspective(
    colour,
    L,
    atmosphericVisibility,
    foregroundAtmosphere
  );
  float particulateAlpha = clamp(alpha, 0.0, 0.98);
  // Custom premultiplied source-over blending lets one fragment express both
  // behaviours.  With no foreground atmosphere this is ordinary alpha
  // compositing.  With opaque foreground air outputAlpha tends to zero while
  // the transmitted ring contribution remains, making it purely additive and
  // therefore incapable of darkening the already-rendered sky.
  float outputAlpha = particulateAlpha * atmosphericVisibility
    * (1.0 - foregroundAtmosphere);
  float sourceWeight = particulateAlpha
    * mix(atmosphericVisibility, 1.0, foregroundAtmosphere);
  gl_FragColor = vec4(colour * sourceWeight, outputAlpha);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class PlanetRingSystem {
  readonly mesh: THREE.Mesh<THREE.RingGeometry, THREE.ShaderMaterial>;
  private readonly uniforms: {
    uColor: THREE.IUniform<THREE.Color>;
    uOpacity: THREE.IUniform<number>;
    uOuterRatio: THREE.IUniform<number>;
    uPlanetRadiusRatio: THREE.IUniform<number>;
    uSunDirection: THREE.IUniform<THREE.Vector3>;
    uSunColor: THREE.IUniform<THREE.Color>;
    uSunIntensity: THREE.IUniform<number>;
    uPassMin: THREE.IUniform<number>;
    uPassMax: THREE.IUniform<number>;
    uPlanetPhysicalRadius: THREE.IUniform<number>;
    uAtmosphereRadiusRatio: THREE.IUniform<number>;
    uScaleHeight: THREE.IUniform<number>;
    uMieScaleHeight: THREE.IUniform<number>;
    uRayleigh: THREE.IUniform<THREE.Vector3>;
    uMie: THREE.IUniform<number>;
    uHazeColor: THREE.IUniform<THREE.Color>;
    uDensityScale: THREE.IUniform<number>;
    uForegroundAtmosphereRendered: THREE.IUniform<number>;
  };

  constructor(body: CelestialBody, angularSegments = 256, cloudRadius = 0) {
    const rings = body.def.rings;
    if (!rings) throw new Error(`PlanetRingSystem requires ring data for ${body.id}`);
    const outerRatio = rings.outerRadius / rings.innerRadius;
    const atmosphere = body.atmosphere;
    const scaleHeight = Math.max(MIN_SCALE_HEIGHT, atmosphere.scaleHeight);
    const densityScale = atmosphere.surfaceDensity > 0
      ? Math.min(DENSITY_SCALE_MAX, Math.max(DENSITY_SCALE_MIN, atmosphere.surfaceDensity / DENSITY_SCALE_NOMINAL))
      : 0;
    this.uniforms = {
      uColor: { value: new THREE.Color(...rings.color) },
      uOpacity: { value: rings.opacity },
      uOuterRatio: { value: outerRatio },
      uPlanetRadiusRatio: { value: body.radius / rings.innerRadius },
      uSunDirection: { value: new THREE.Vector3(1, 0, 0) },
      uSunColor: { value: new THREE.Color(...SUN_COLOR) },
      uSunIntensity: { value: SUN_INTENSITY_DEFAULT },
      uPassMin: { value: 0 },
      uPassMax: { value: 1e30 },
      uPlanetPhysicalRadius: { value: body.radius },
      uAtmosphereRadiusRatio: { value: (body.radius + body.atmosphereTop) / body.radius },
      uScaleHeight: { value: scaleHeight },
      uMieScaleHeight: { value: Math.max(MIN_SCALE_HEIGHT, scaleHeight * MIE_SCALE_HEIGHT_FRACTION) },
      uRayleigh: {
        value: new THREE.Vector3(atmosphere.rayleigh[0], atmosphere.rayleigh[1], atmosphere.rayleigh[2]),
      },
      uMie: { value: atmosphere.mie },
      uHazeColor: {
        value: new THREE.Color(atmosphere.hazeColor[0], atmosphere.hazeColor[1], atmosphere.hazeColor[2]),
      },
      uDensityScale: { value: densityScale },
      // AstronomicalLayer leaves cloudRadius at zero: its compressed render
      // coordinates cannot be mixed with physical cloud metres. CloudLayer
      // supplies the real local deck radius and updates the fixed basis.
      // A non-zero cloud radius identifies the real-scale local renderer. Its
      // atmosphere shell is present in the world scene; the compressed
      // astronomical renderer instead has to integrate its own foreground air.
      uForegroundAtmosphereRendered: { value: cloudRadius > 0 ? 1 : 0 },
    };

    const material = new THREE.ShaderMaterial({
      uniforms: this.uniforms as unknown as Record<string, THREE.IUniform>,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
    });
    material.forceSinglePass = true;

    const geometry = new THREE.RingGeometry(1, outerRatio, angularSegments, 1);
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'planet-rings';
    this.mesh.frustumCulled = false;
    this.mesh.scale.setScalar(rings.innerRadius);
    this.mesh.onBeforeRender = (_renderer, _scene, camera) => {
      this.uniforms.uPassMin.value = Number(camera.userData.transparentPassMin ?? 0);
      this.uniforms.uPassMax.value = Number(camera.userData.transparentPassMax ?? 1e30);
    };
  }

  setLighting(direction: Vec3, color: THREE.Color, intensity: number): void {
    this.uniforms.uSunDirection.value.set(direction.x, direction.y, direction.z);
    this.uniforms.uSunColor.value.copy(color);
    this.uniforms.uSunIntensity.value = intensity;
  }


  dispose(): void {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.mesh.removeFromParent();
  }
}
